import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import type { Zone } from "../types";
import {
  selectedZoneIdsAtom,
  activeZoneIdAtom,
  zonesByIdAtom,
  preloadZonesAtom,
  upsertZonesAtom,
} from "../atoms";

export function useZones() {
  const [zoneIds, setZoneIds] = useAtom(selectedZoneIdsAtom);
  const [activeZoneId, _setActiveZoneId] = useAtom(activeZoneIdAtom);
  const [zonesById] = useAtom(zonesByIdAtom);
  const [, preloadZones] = useAtom(preloadZonesAtom as any);
  const [, upsertZones] = useAtom(upsertZonesAtom as any);

  // Idempotent setter
  const setActiveZoneId = useCallback(
    (next: string | null) => {
      if (next === activeZoneId) return;
      _setActiveZoneId(next);
    },
    [activeZoneId, _setActiveZoneId]
  );

  // Selected zones memo
  const selectedZones = useMemo<Zone[]>(
    () => zoneIds.map((id) => zonesById.get(id)).filter(Boolean) as Zone[],
    [zoneIds, zonesById]
  );

  // Preload missing zones
  useEffect(() => {
    if (zoneIds.length) preloadZones(zoneIds);
  }, [zoneIds, preloadZones]);

  // keep activeZoneId valid
  useEffect(() => {
    if (activeZoneId && !zoneIds.includes(activeZoneId)) {
      _setActiveZoneId(null);
      return;
    }
    if (!activeZoneId && zoneIds.length > 0) {
      _setActiveZoneId(zoneIds[0]);
    }
  }, [zoneIds, activeZoneId, _setActiveZoneId]);

  // Single add/remove
  const addAndActivateZone = useCallback(
    (zone: Zone | string) => {
      if (typeof zone !== "string") {
        upsertZones([zone]);
      }
      const id = typeof zone === "string" ? zone : zone.id;
      setZoneIds((cur) => (cur.includes(id) ? cur : [id, ...cur]));
      if (!activeZoneId) _setActiveZoneId(id);
    },
    [setZoneIds, activeZoneId, _setActiveZoneId, upsertZones]
  );

  const removeZone = useCallback(
    (idRaw: string) => {
      const id = String(idRaw);
      setZoneIds((cur) => cur.filter((x) => x !== id));
      if (activeZoneId === id) _setActiveZoneId(null);
    },
    [setZoneIds, activeZoneId, _setActiveZoneId]
  );

  // Multi add/remove
  const addAndActivateZones = useCallback(
    (zones: Zone[]) => {
      if (!zones.length) return;
      upsertZones(zones);
      const ids = zones.map((z) => String(z.id));
      setZoneIds((cur) => {
        const set = new Set(cur);
        for (const id of ids) set.add(id);
        return Array.from(set);
      });
      if (!activeZoneId) _setActiveZoneId(ids[0]);
    },
    [upsertZones, setZoneIds, activeZoneId, _setActiveZoneId]
  );

  const removeZones = useCallback(
    (ids: string[]) => {
      if (!ids.length) return;
      setZoneIds((cur) => cur.filter((x) => !ids.includes(x)));
    },
    [setZoneIds]
  );

  return {
    zoneIds,
    setZoneIds,
    selectedZones,
    activeZoneId,
    setActiveZoneId,
    addAndActivateZone,
    removeZone,
    addAndActivateZones,
    removeZones,
    isLoading: false,
  };
}
